import { createFileRoute, Link } from "@tanstack/react-router";
import { buildSeo } from "@/lib/seo";
import { MarketingLayout, Eyebrow } from "@/components/marketing/Layout";
import { useAutoTranslate } from "@/components/AutoTranslate";
import { FEATURE_GROUPS, type FeatureStatus } from "@/lib/feature-status";
import { ArrowRight, Check, CircleDot, Circle } from "lucide-react";

export const Route = createFileRoute("/roadmap")({
  head: () =>
    buildSeo({
      path: "/roadmap",
      title: "Roadmap: what is live, beta and planned | Reality Twin",
      description:
        "We build in the open. Every Reality Twin capability, honestly marked Live, Beta or Planned — and we never sell a planned feature as present.",
    }),
  component: RoadmapPage,
});

const STATUS_META: Record<
  FeatureStatus,
  { icon: typeof Check; cls: string; ring: string; key: number }
> = {
  live: {
    icon: Check,
    cls: "text-emerald-600",
    ring: "bg-emerald-500/10 border-emerald-500/30",
    key: 0,
  },
  beta: {
    icon: CircleDot,
    cls: "text-amber-600",
    ring: "bg-amber-500/10 border-amber-500/30",
    key: 1,
  },
  planned: {
    icon: Circle,
    cls: "text-muted-foreground",
    ring: "bg-muted/40 border-border",
    key: 2,
  },
};

function RoadmapPage() {
  const CHROME = [
    "Build in the open", // 0
    "What's live, what's next — honestly.", // 1
    "Most companies hide their roadmap. We publish it. Every capability below is marked Live, Beta or Planned — and we never sell a planned feature as present.", // 2
    "Live", // 3
    "Beta", // 4
    "Planned", // 5
    "Shipped and working today.", // 6
    "Shipping, but new — expect rough edges.", // 7
    "Designed, not built yet.", // 8
    "See how it works", // 9
    "Run a free Twin-Check", // 10
  ];
  const groupNames = FEATURE_GROUPS.map((g) => g.group);
  const blurbs = FEATURE_GROUPS.map((g) => g.blurb);
  const featNames = FEATURE_GROUPS.flatMap((g) => g.features.map((f) => f.name));
  const notes = FEATURE_GROUPS.flatMap((g) => g.features.map((f) => f.note ?? ""));

  const strings = [...CHROME, ...groupNames, ...blurbs, ...featNames, ...notes];
  const { out } = useAutoTranslate(strings);
  let i = 0;
  const C = CHROME.map(() => out[i++]);
  const groupNamesT = groupNames.map(() => out[i++]);
  const blurbsT = blurbs.map(() => out[i++]);
  const featNamesT = featNames.map(() => out[i++]);
  const notesT = notes.map(() => out[i++]);
  const statusLabel = (s: FeatureStatus) => C[3 + STATUS_META[s].key];

  const counts = { live: 0, beta: 0, planned: 0 };
  FEATURE_GROUPS.forEach((g) => g.features.forEach((f) => (counts[f.status] += 1)));

  let fi = 0; // running index into the flattened feature arrays

  return (
    <MarketingLayout>
      <section className="border-b border-border">
        <div className="mx-auto max-w-4xl px-6 pt-20 pb-12 sm:pt-28">
          <Eyebrow>{C[0]}</Eyebrow>
          <h1 className="mt-4 font-serif text-[42px] sm:text-[60px] leading-[1.0] tracking-[-0.035em]">
            {C[1]}
          </h1>
          <p className="mt-5 text-[17px] leading-[1.6] text-muted-foreground max-w-2xl">{C[2]}</p>

          <div className="mt-7 flex flex-wrap gap-2.5 text-[12.5px]">
            {(["live", "beta", "planned"] as FeatureStatus[]).map((s) => {
              const m = STATUS_META[s];
              const Icon = m.icon;
              const n = counts[s];
              return (
                <span
                  key={s}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 ${m.ring}`}
                >
                  <Icon className={`h-3.5 w-3.5 ${m.cls}`} strokeWidth={2.5} />
                  <span className="font-medium">{statusLabel(s)}</span>
                  <span className="text-muted-foreground">· {n}</span>
                </span>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-14 sm:py-16">
        <div className="mx-auto max-w-4xl px-6 space-y-12">
          {FEATURE_GROUPS.map((g, gi) => (
            <div key={g.group}>
              <div className="flex items-baseline justify-between gap-4 border-b border-border pb-3">
                <h2 className="font-serif text-[26px] leading-tight tracking-[-0.02em]">
                  {groupNamesT[gi] ?? g.group}
                </h2>
                <span className="text-[13px] text-muted-foreground shrink-0 hidden sm:block">
                  {blurbsT[gi] ?? g.blurb}
                </span>
              </div>
              <div className="mt-1 divide-y divide-border">
                {g.features.map((f) => {
                  const idx = fi++;
                  const m = STATUS_META[f.status];
                  const Icon = m.icon;
                  return (
                    <div key={f.name} className="flex items-start gap-3 py-3.5">
                      <span
                        className={`mt-0.5 h-6 w-6 shrink-0 rounded-full border flex items-center justify-center ${m.ring}`}
                      >
                        <Icon className={`h-3.5 w-3.5 ${m.cls}`} strokeWidth={2.5} />
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="text-[15px] leading-snug">
                          {featNamesT[idx] ?? f.name}
                          <span
                            className={`ml-2 align-middle text-[10px] uppercase tracking-wide ${m.cls}`}
                          >
                            {statusLabel(f.status)}
                          </span>
                        </div>
                        {f.note && (
                          <div className="text-[12.5px] text-muted-foreground mt-0.5">
                            {notesT[idx] || f.note}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          <div className="hairline rounded-2xl bg-cream p-6 sm:p-8 flex flex-wrap items-center gap-3">
            <Link
              to="/platform"
              className="inline-flex items-center gap-1.5 rounded-full bg-foreground px-4 py-2 text-[13.5px] font-medium text-background hover:opacity-90"
            >
              {C[9]} <ArrowRight className="h-3.5 w-3.5" strokeWidth={2} />
            </Link>
            <Link
              to="/twin-check"
              className="inline-flex items-center rounded-full hairline bg-background px-4 py-2 text-[13.5px]"
            >
              {C[10]}
            </Link>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
